// Single-simulator practice surface inside the Equipment Lab.
//
// Renders a registered Tier 2 simulator at full canvas (breaks out of
// the <main> 920px max-width). Mounts <ScenarioProvider> + picker for
// simulators that support scenarios. When ?walkthrough=<id> resolves
// to a registry entry for this kind, mounts a side-docked coach panel
// next to the simulator and pipes the simulator's onAction stream
// into the walkthrough engine for auto-advance.

import { useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import EquipmentSimulator from "../components/EquipmentSimulator";
import { MiniSwitcher } from "../components/simulators/mini-switcher";
import { SIMULATORS, SIMULATOR_KINDS, type SimulatorKind } from "../components/simulators";
import { ScenarioProvider } from "../components/simulators/scene/ScenarioContext";
import ScenarioPicker from "../components/simulators/scene/ScenarioPicker";
import { AVAILABLE_SCENARIO_IDS } from "../components/simulators/scene/scenarios";
import type { ScenarioId } from "../components/simulators/scene/types";
import type { MultiviewBusSource } from "../components/simulators/multiview-layout";
import { findWalkthrough } from "../components/simulators/help-data/walkthroughs";
import WalkthroughCoach, {
  useWalkthroughEngine,
} from "../components/help/walkthrough-coach";
import { EmbedContextProvider } from "../components/help/embed-context";

function isSimulatorKind(value: string | undefined): value is SimulatorKind {
  return !!value && (SIMULATOR_KINDS as readonly string[]).includes(value);
}

function isScenarioId(value: string | null): value is ScenarioId {
  return !!value && (AVAILABLE_SCENARIO_IDS as readonly string[]).includes(value);
}

export default function EquipmentLabPanel() {
  const { kind } = useParams<{ kind: string }>();
  const [searchParams] = useSearchParams();

  if (!isSimulatorKind(kind)) {
    return <Navigate to="/lab" replace />;
  }

  const entry = SIMULATORS[kind];
  const scenarioParam = searchParams.get("scenario");
  const initialScenarioId = isScenarioId(scenarioParam) ? scenarioParam : undefined;

  const walkthroughParam = searchParams.get("walkthrough");
  const walkthrough = walkthroughParam
    ? findWalkthrough(kind, walkthroughParam)
    : undefined;

  return (
    <div className="lab-panel">
      <header className="lab-panel__header">
        <Link to="/lab" className="lab-panel__back">
          ← All equipment
        </Link>
        <div className="lab-panel__title-block">
          <p className="lab-panel__eyebrow">
            <span data-crew={entry.crew}>{entry.crew.toUpperCase()}</span>
            {" · "}EQUIPMENT LAB
          </p>
          <h1>{entry.label}</h1>
          <p className="lab-panel__hardware">{entry.hardware}</p>
        </div>
      </header>

      <EmbedContextProvider value="lab">
        {entry.supportsScenarios ? (
          <ScenarioProvider initialId={initialScenarioId}>
            <LabBody kind={kind} entry={entry} walkthrough={walkthrough} />
          </ScenarioProvider>
        ) : (
          <LabBody kind={kind} entry={entry} walkthrough={walkthrough} />
        )}
      </EmbedContextProvider>

      <style>{LAB_PANEL_STYLES}</style>
    </div>
  );
}

interface LabBodyProps {
  kind: SimulatorKind;
  entry: (typeof SIMULATORS)[SimulatorKind];
  walkthrough: ReturnType<typeof findWalkthrough>;
}

function LabBody({ kind, entry, walkthrough }: LabBodyProps) {
  // Practice switcher state — only used for PTZ so trainees can simulate
  // TD workflow. Not part of the real PTZ hardware.
  const [pgmSource, setPgmSource] = useState<MultiviewBusSource>("cam2");
  const [pvwSource, setPvwSource] = useState<MultiviewBusSource>("cam1");
  const isPTZ = kind === "ptz-aw-rp50";

  const sim = walkthrough ? (
    <SimWithCoach kind={kind} walkthrough={walkthrough} />
  ) : isPTZ ? (
    <EquipmentSimulator
      kind={kind}
      initialState={{
        pgmSource,
        pvwSource,
      }}
    />
  ) : (
    <EquipmentSimulator kind={kind} />
  );

  return (
    <div className="lab-panel__canvas">
      <div className="lab-panel__sim-stack">
        {entry.supportsScenarios ? (
          <div className="lab-panel__scenario-bar">
            <ScenarioPicker />
          </div>
        ) : null}
        {isPTZ && (
          <MiniSwitcher
            pgmSource={pgmSource}
            pvwSource={pvwSource}
            onPvwChange={setPvwSource}
            onTake={() => setPgmSource(pvwSource)}
          />
        )}
        {sim}
      </div>
    </div>
  );
}

interface SimWithCoachProps {
  kind: SimulatorKind;
  walkthrough: NonNullable<ReturnType<typeof findWalkthrough>>;
}

function SimWithCoach({ kind, walkthrough }: SimWithCoachProps) {
  const engine = useWalkthroughEngine(walkthrough.steps);
  return (
    <div className="lab-panel__sim-and-coach">
      <div className="lab-panel__sim-slot">
        <EquipmentSimulator kind={kind} onAction={engine.handleAction} />
      </div>
      <aside className="lab-panel__coach-slot">
        <WalkthroughCoach title={walkthrough.title} engine={engine} />
      </aside>
    </div>
  );
}

const LAB_PANEL_STYLES = `
.lab-panel {
  /* Break out of <main>'s 920px max-width so the simulator renders at
     native scale. */
  width: 100vw;
  margin-left: calc(50% - 50vw);
  padding: 1.5rem 2rem 3rem;
  box-sizing: border-box;
  display: flex; flex-direction: column; gap: 1.25rem;
  overflow-x: hidden;
}
.lab-panel__header {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}
.lab-panel__back {
  font-size: 13px; letter-spacing: 0.5px; color: #6e6452;
  text-decoration: none;
}
.lab-panel__back:hover { color: #2a261d; }
.lab-panel__title-block { margin-top: 0.5rem; }
.lab-panel__eyebrow {
  font-size: 10px; letter-spacing: 2px; font-weight: 700;
  color: #6e6452; margin: 0 0 0.25rem;
}
.lab-panel__eyebrow [data-crew="video"] { color: #c46a1f; }
.lab-panel__eyebrow [data-crew="audio"] { color: #2d6a4f; }
.lab-panel__eyebrow [data-crew="stage"] { color: #5a4a8a; }
.lab-panel__eyebrow [data-crew="it"] { color: #8a4a6a; }
.lab-panel h1 { margin: 0; font-size: 28px; }
.lab-panel__hardware {
  margin: 0.25rem 0 0; font-size: 13px; color: #6e6452;
}
.lab-panel__canvas {
  display: flex; justify-content: center;
  width: 100%;
  min-width: 0;
}
.lab-panel__sim-stack {
  display: flex; flex-direction: column; align-items: center;
  gap: 12px; width: 100%;
}
.lab-panel__scenario-bar {
  display: flex; justify-content: center;
}
.lab-panel__sim-and-coach {
  display: flex; flex-direction: row; align-items: flex-start;
  gap: 24px;
  width: 100%;
  justify-content: center;
}
.lab-panel__sim-slot {
  flex: 0 1 auto;
  min-width: 0;
}
.lab-panel__coach-slot {
  flex: 0 0 auto;
}
@media (max-width: 1100px) {
  .lab-panel__sim-and-coach { flex-direction: column; align-items: center; }
}
`;
